import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './TournamentBracket.module.css';

interface Participant {
  id?: number;
  name: string;
  type?: string;
}

interface Match {
  id: number;
  team1Participants: Participant[];
  team2Participants: Participant[];
  winner_team?: number;
  round_number: number;
  battle_report?: number;
  match_type?: 'normal' | 'final' | 'third_place' | 'bye';
}

interface BracketProps {
  matches: Match[];
  activeMercenaryId?: number;
}

type RoundKey = string;

interface RoundTab {
  key: RoundKey;
  label: string;
  matches: Match[];
  isSpecial: boolean;
}

const buildRoundTabs = (matches: Match[]): RoundTab[] => {
  // Separate special matches
  const finalMatches = matches.filter(m => m.match_type === 'final');
  const thirdPlace = matches.filter(m => m.match_type === 'third_place');
  const normalMatches = matches.filter(
    m => m.match_type !== 'final' && m.match_type !== 'third_place'
  );

  // Group normal/bye matches by round number
  const byRound: Record<number, Match[]> = {};
  normalMatches.forEach(m => {
    if (!byRound[m.round_number]) byRound[m.round_number] = [];
    byRound[m.round_number].push(m);
  });

  const roundNumbers = Object.keys(byRound)
    .map(Number)
    .sort((a, b) => a - b);

  const totalNormalRounds = roundNumbers.length;

  const tabs: RoundTab[] = roundNumbers.map((rn, idx) => {
    let label: string;
    if (totalNormalRounds >= 3 && idx === totalNormalRounds - 1) {
      label = 'Semi-Finals';
    } else if (totalNormalRounds === 2 && idx === totalNormalRounds - 1) {
      label = 'Semi-Finals';
    } else if (totalNormalRounds === 1) {
      label = `Round ${rn}`;
    } else {
      label = `Round ${rn}`;
    }
    return { key: `round_${rn}`, label, matches: byRound[rn], isSpecial: false };
  });

  if (thirdPlace.length > 0) {
    tabs.push({ key: 'third', label: '3rd Place', matches: thirdPlace, isSpecial: true });
  }
  if (finalMatches.length > 0) {
    tabs.push({ key: 'final', label: 'Grand Final', matches: finalMatches, isSpecial: true });
  }

  return tabs;
};

const MatchCard: React.FC<{ match: Match; activeMercenaryId?: number }> = ({ match, activeMercenaryId }) => {
  const isBye = match.match_type === 'bye';
  const isFinal = match.match_type === 'final';
  const isThird = match.match_type === 'third_place';
  const winner = match.winner_team;
  const pending = !winner && !isBye;

  const isActiveMercInMatch = activeMercenaryId != null && (
    match.team1Participants.some(p => p.id === activeMercenaryId) ||
    match.team2Participants.some(p => p.id === activeMercenaryId)
  );

  if (isBye) {
    return (
      <div className={`${styles.matchCard} ${styles.byeCard}`}>
        <div className={styles.byeBadge}>BYE</div>
        <div className={`${styles.teamRow} ${styles.winner}`}>
          {match.team1Participants.filter(p => p.name).map(p => (
            <span key={p.id ?? p.name} className={styles.pName}>{p.name}</span>
          ))}
        </div>
        <div className={styles.byeNote}>Advances automatically</div>
      </div>
    );
  }

  return (
    <div className={`${styles.matchCard} ${isFinal ? styles.finalCard : ''} ${isThird ? styles.thirdCard : ''} ${pending ? styles.pendingCard : ''} ${isActiveMercInMatch ? styles.activeMercCard : ''}`}>
      {isFinal && <div className={styles.matchTypeBadge + ' ' + styles.finalBadge}>FINAL</div>}
      {isThird && <div className={styles.matchTypeBadge + ' ' + styles.thirdBadge}>3RD PLACE</div>}

      {/* Team 1 */}
      <div className={`${styles.teamRow} ${winner === 1 ? styles.winner : ''} ${winner === 2 ? styles.loser : ''}`}>
        <div className={styles.teamNames}>
          {match.team1Participants.filter(p => p.name).map(p => (
            <span key={p.id ?? p.name} className={styles.pName}>{p.name}</span>
          ))}
        </div>
      </div>

      <div className={styles.vsRow}>
        {pending ? (
          <span className={styles.vsPending}>VS</span>
        ) : (
          <span className={styles.vsText}>VS</span>
        )}
      </div>

      {/* Team 2 */}
      <div className={`${styles.teamRow} ${winner === 2 ? styles.winner : ''} ${winner === 1 ? styles.loser : ''}`}>
        <div className={styles.teamNames}>
          {match.team2Participants.filter(p => p.name).map(p => (
            <span key={p.id ?? p.name} className={styles.pName}>{p.name}</span>
          ))}
        </div>
      </div>

      {match.battle_report && (
        <Link to={`/battle-result/${match.battle_report}`} className={styles.reportLink}>
          View Battle Report
        </Link>
      )}
    </div>
  );
};

const TournamentBracket: React.FC<BracketProps> = ({ matches, activeMercenaryId }) => {
  const tabs = buildRoundTabs(matches);
  const [activeTab, setActiveTab] = useState<RoundKey>(tabs[0]?.key ?? '');

  // If new matches arrive (ongoing tournament), keep active tab valid
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [matches]);

  if (tabs.length === 0) {
    return <p className={styles.empty}>No matches available yet.</p>;
  }

  const current = tabs.find(t => t.key === activeTab) ?? tabs[0];

  return (
    <div className={styles.bracket}>
      {/* Round Tabs */}
      <div className={styles.roundTabs}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`${styles.roundTab} ${activeTab === tab.key ? styles.activeTab : ''} ${tab.isSpecial ? styles.specialTab : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className={styles.roundMatchCount}>{tab.matches.length}</span>
          </button>
        ))}
      </div>

      {/* Match Grid */}
      <div className={styles.matchGrid}>
        {current.matches.map(match => (
          <MatchCard key={match.id} match={match} activeMercenaryId={activeMercenaryId} />
        ))}
      </div>
    </div>
  );
};

export default TournamentBracket;
